import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Card} from "primereact/card";
import {Tag} from "primereact/tag";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";
import {API} from "./App";
import {getAuthHeader} from "./AuthRoute";

function ProjectsDashboard() {
    const navigate = useNavigate();

    const [showCreateProjectDialog, setShowCreateProjectDialog] = useState(false);
    const [dialogProjectName, setDialogProjectName] = useState("");
    const [dialogProjectDesc, setDialogProjectDesc] = useState("");
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const [projects, setProjects] = useState([]);
    const [deletingProject, setDeletingProject] = useState(null);

    const refreshProjects = () => {
        fetch(API() + "/projects", {
            method: "GET",
            headers: {
                Authorization: getAuthHeader(),
            },
        })
            .then((response) => {
                if (response.status === 200) {
                    return response.json();
                } else {
                    throw new Error("Failed to fetch projects");
                }
            })
            .then((data) => {
                setProjects(data);
            })
            .catch((error) => {
                alert(error);
            });
    };

    useEffect(() => {
        refreshProjects();
    }, []);

    const handleProjectClick = (projectName) => {
        navigate(`/${projectName}/project/1`);
    };

    const handleCreateProjectClick = () => {
        setShowCreateProjectDialog(true);
    };

    const handleCloseDialog = () => {
        setShowCreateProjectDialog(false);
        setDialogProjectName("");
        setDialogProjectDesc("");
        setErrors({});
    };

    const handleCreateProject = async () => {
        let formErrors = {};

        if (!dialogProjectName) {
            formErrors.name = "Project name is required.";
        }

        if (!dialogProjectDesc) {
            formErrors.description = "Project description is required.";
        }

        setErrors(formErrors);

        let noErrors = Object.keys(formErrors).length === 0;
        if (noErrors) {
            setIsLoading(true);
            fetch(API() + "/projects", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: getAuthHeader(),
                },
                body: JSON.stringify({
                    project_name: dialogProjectName,
                    description: dialogProjectDesc,
                }),
            })
                .then((response) => {
                    if (response.status === 201) {
                        handleCloseDialog();
                        refreshProjects();
                    } else {
                        return response.text().then((errorMessage) => {
                            throw new Error(errorMessage);
                        });
                    }
                })
                .catch((error) => {
                    setErrors({api: error.message});
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    };

    const handleDeleteProject = (projectName, e) => {
        e.stopPropagation();
        if (!window.confirm(`Delete project "${projectName}"? This will mark it invisible.`)) {
            return;
        }
        setDeletingProject(projectName);

        fetch(API() + `/projects/${encodeURIComponent(projectName)}/visibility`, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
                Authorization: getAuthHeader(),
            },
            body: JSON.stringify({visible: false}),
        })
            .then((response) => {
                if (response.status === 200) {
                    refreshProjects();
                } else {
                    return response.text().then((msg) => {
                        throw new Error(msg || "Failed to delete project");
                    });
                }
            })
            .catch((err) => {
                setErrors({api: err.message});
            })
            .finally(() => {
                setDeletingProject(null);
            });
    };

    let createNewProjectComponent = (<>
        <Dialog
            header="Create New Project"
            visible={showCreateProjectDialog}
            style={{width: "400px"}}
            footer={
                <div>
                    <Button
                        label="Cancel"
                        icon="pi pi-times"
                        className="p-button-text"
                        onClick={handleCloseDialog}
                        disabled={isLoading}
                    />
                    <Button
                        label={isLoading ? "Creating..." : "Create"}
                        icon={isLoading ? "pi pi-spin pi-spinner" : "pi pi-check"}
                        className="p-button-success"
                        onClick={handleCreateProject}
                        disabled={isLoading}
                    />
                </div>
            }
            onHide={handleCloseDialog}
        >
            <div className="p-fluid">
                <div className="p-field">
                    <label htmlFor="projectName">Project Name</label>
                    <InputText
                        id="projectName"
                        value={dialogProjectName}
                        onChange={(e) => setDialogProjectName(e.target.value)}
                        className={errors.name ? "p-invalid" : ""}
                    />
                    {errors.name && <small className="p-error">{errors.name}</small>}
                </div>
                <div className="p-field">
                    <label htmlFor="projectDescription">Project Description</label>
                    <InputText
                        id="projectDescription"
                        value={dialogProjectDesc}
                        onChange={(e) => setDialogProjectDesc(e.target.value)}
                        className={errors.description ? "p-invalid" : ""}
                    />
                    {errors.description && (<small className="p-error">{errors.description}</small>)}
                </div>
                {errors.api && <small className="p-error">{errors.api}</small>}
            </div>
        </Dialog>
    </>);

    return (
        <div className="projects-dashboard" style={{padding: "20px"}}>
            <h2>Projects Dashboard</h2>
            <Button
                label="Create New Project"
                icon="pi pi-plus"
                className="p-button-success"
                style={{marginBottom: "20px"}}
                onClick={handleCreateProjectClick}
            />
            <div
                className="projects-grid"
                style={{display: "flex", flexWrap: "wrap", gap: "20px"}}
            >
                {projects.map((project) => {
                    const total = project.sondages_ids.length;
                    const found = project.sondages_ids.filter((id) => id !== null).length;
                    const tagLabel = found === total ? `${total} sondages` : `${found}/${total} sondages found`;

                    return (<Card
                        key={project.project_name}
                        title={project.project_name}
                        className="project-card"
                        style={{width: "300px", cursor: "pointer"}}
                        onClick={() => handleProjectClick(project.project_name)}
                    >
                        <p>{project.description || "No description provided"}</p>
                        <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                            <Tag value={tagLabel} severity="info"/>
                            <Button
                                icon={deletingProject === project.project_name ? "pi pi-spin pi-spinner" : "pi pi-trash"}
                                className={`delete-btn p-button-danger p-button-sm`}
                                onClick={(e) => handleDeleteProject(project.project_name, e)}
                                disabled={deletingProject === project.project_name}
                            />
                        </div>
                    </Card>);
                })}
            </div>

            {createNewProjectComponent}
        </div>);
}

export default ProjectsDashboard;
